'use client';
import { useCallback, use } from 'react';
import i18next from 'i18next';
import {
  initReactI18next,
  useTranslation as useTranslationBase
} from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import resourcesToBackend from 'i18next-resources-to-backend';
import ChainedBackend from 'i18next-chained-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { getOptions, languages, makeOrdinal } from './settings';
import helper from './postprocess-helper';
import { setCookie } from '../utils';

const isServerSideRender = typeof window === 'undefined';

i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(ChainedBackend)
  .use(helper)
  .init({
    ...getOptions(),
    lng: undefined, // let detect the language on client side
    detection: {
      order: ['querystring', 'path', 'cookie', 'htmlTag', 'navigator']
    },
    preload: isServerSideRender ? languages : [],
    backend: {
      backends: [
        HttpBackend,
        resourcesToBackend(
          (lng, ns) => import(`@/../public/locales/${lng}/${ns}.json`)
        )
      ],
      backendOptions: [
        {
          loadPath: '/locales/{{lng}}/{{ns}}.json'
        }
      ]
    }
  });

export function useTranslation(lng, ns, options) {
  const translation = useTranslationBase(ns, options);
  if (isServerSideRender && i18next.resolvedLanguage !== lng) {
    use(i18next.changeLanguage(lng));
  }

  const changeLanguage = useCallback((lng) => {
    if (!isServerSideRender) {
      setCookie('i18next', lng, 365);
      i18next.changeLanguage(lng);
    }
  }, []);

  const ordinal = useCallback((n) => makeOrdinal(i18next.t, n), []);

  return {
    ...translation,
    i18n: i18next,
    lng: i18next.resolvedLanguage,
    changeLanguage,
    ordinal
  };
}
